/* global React, Appear, Counter, useInView, PROJECTS, REVIEWS_BANK, OrbAle */
const { useState, useEffect, useRef } = React;

// Render text as mono base with last word in handwriting accent.
// Used to keep handwriting strictly as an accent (max 1 word), per type rules.
function MonoWithAccent({ text }) {
  const parts = String(text).split(" ");
  const last = parts.pop();
  return (
    <React.Fragment>
      {parts.length > 0 && <span>{parts.join(" ")} </span>}
      <span className="accent">{last}</span>
    </React.Fragment>
  );
}

// ─── Hero (after profile pick) ─────────────────────────────────────────────
function Hero({ profile, content }) {
  const baja = content.heroBajada[profile] || content.heroBajada.neutral;
  const ht = content.heroTitle;
  return (
    <section className="hero shell">
      <div className="hero-eyebrow">
        <span className="tag" style={{ color: "var(--ink-3)" }}>
          {content.heroEyebrow}
        </span>
      </div>
      <h1 className="hero-title">
        <span className="reveal-line">
          <span>
            {ht.pre}<span className="case-hand">{ht.accent}</span>{ht.post}
          </span>
        </span>
      </h1>
      <p className="hero-baja">{baja}</p>
      <div className="hero-meta">
        <span>📍 <b>{content.heroMeta.based}</b></span>
      </div>
    </section>
  );
}

// ─── Bento ─────────────────────────────────────────────────────────────────
// Orden de proyectos en el bento del home — los 2 únicos casos con
// contenido. El resto vive comentado en content.jsx (PROJECTS) hasta que
// estén listos. Se renderizan ambos como `is-featured` (mismo tamaño,
// lado a lado) — la jerarquía la marca el orden: DJY Pro primero.
const FEATURED_BENTO_IDS = ["djy", "ai"];

function Bento({ profile, content, projects, lang, onOpenCase }) {
  const featuredCards = FEATURED_BENTO_IDS
    .map((id) => projects.find((p) => p.id === id))
    .filter(Boolean);

  const handleOpen = (id) => {
    if (!onOpenCase) return;
    if (id === "djy") onOpenCase("djy");
    else if (id === "ai") onOpenCase("ale");
  };

  // Helper: resolve a field that may be either a plain string (legacy) or a
  // { es, en } object (lang-aware).
  const L = (v) => (v && typeof v === "object" && (v.es || v.en) ? (v[lang] || v.es || v.en) : v);
  const labels = content.bentoLabels || { result: "resultado", role: "rol" };

  return (
    <section className="section shell" id="projects">
      <div className="section-head">
        <h2>{content.sections.projects.title}</h2>
        <span className="tag">{content.sections.projects.tag}</span>
      </div>
      <Appear className="bento bento--pair">
        {featuredCards.map((card) => (
          <button
            key={card.id}
            className={`bento-card is-featured${card.id === "ai" ? " is-orb" : ""}`}
            onClick={() => handleOpen(card.id)}
          >
            <span className="preview">
              {card.id === "djy" && (
                <picture>
                  <source srcSet={encodeURI("assets/cases/preview-cards/card DJY pro.webp")} type="image/webp" />
                  <img
                    src={encodeURI("assets/cases/preview-cards/card DJY pro.png")}
                    alt=""
                    loading="lazy"
                    decoding="async"
                  />
                </picture>
              )}
              {card.id === "ai" && (
                <OrbAle className="orb-ale--card-featured" />
              )}
            </span>
            <div className="content">
              <div>
                <div className="meta">
                  <span>{L(card.industry)}</span>
                  <span>{card.year}</span>
                </div>
                <h4 style={{ marginTop: 12 }}>{L(card.title)}</h4>
                {card.description && (
                  <p className="featured-desc">{L(card.description)}</p>
                )}
              </div>
              <div className="featured-foot">
                <div className="kpis">
                  <div>
                    <b>{L(card.result)}</b>
                    <span>{labels.result}</span>
                  </div>
                  <div>
                    <b>{L(card.role)}</b>
                    <span>{labels.role}</span>
                  </div>
                </div>
              </div>
            </div>
          </button>
        ))}
      </Appear>
    </section>
  );
}

// ─── Bio (home) — extracto simple, la historia completa vive en /sobre-mi ──
function Bio({ profile, content, onOpenAbout, onOpenDesborde }) {
  // Resalto una palabra del último ledePara en handwriting accent.
  // La palabra se configura por idioma vía content.bio.accentWord (no
  // necesariamente al final — funciona en cualquier posición).
  const paras = content.bio.ledeParas || [content.bio.lede];
  const accentWord = content.bio.accentWord;
  const parasRender = paras.map((p, i) => {
    const isLast = i === paras.length - 1;
    if (isLast && accentWord && p.includes(accentWord)) {
      const idx = p.indexOf(accentWord);
      return (
        <p key={i}>
          {p.slice(0, idx)}
          <span className="case-hand">{accentWord}</span>
          {p.slice(idx + accentWord.length)}
        </p>
      );
    }
    return <p key={i}>{p}</p>;
  });
  return (
    <section className="section shell" id="bio">
      <div className="section-head">
        <h2>{content.sections.bio.title}</h2>
        <span className="tag">{content.sections.bio.tag}</span>
      </div>
      <Appear className="bio">
        <div className="bio-photo">
          <picture>
            <source srcSet={encodeURI("assets/foto-anita.webp")} type="image/webp" />
            <img
              src={encodeURI("assets/Foto_Anita.png")}
              alt={content.bio.photoAlt || "Anita Sevilla Ull, retrato — sonriendo, pelo largo oscuro, remera verde clara, fondo de ventana abierta con vegetación."}
              loading="lazy"
              decoding="async"
            />
          </picture>
        </div>
        <div className="bio-body">
          {parasRender}
          {content.bio.chips && content.bio.chips.length > 0 && (
            <div className="bio-chips">
              {content.bio.chips.map((c) => (
                <span key={c} className="chip">{c}</span>
              ))}
            </div>
          )}
          <div className="bio-cta-row">
            <button className="btn" onClick={onOpenAbout}>
              {content.bio.cta} <span className="arrow">→</span>
            </button>
            {onOpenDesborde && (
              <button className="btn btn--ghost" onClick={onOpenDesborde}>
                {content.bio.ctaSecondary}
              </button>
            )}
          </div>
        </div>
      </Appear>
    </section>
  );
}

// ─── Comunidad ─────────────────────────────────────────────────────────────
function Comunidad({ content }) {
  return (
    <section className="section shell" id="community">
      <div className="section-head">
        {/* El "título" visual es el logo CUXUI (más abajo); acá dejo un h2
            accesible visualmente oculto para que la jerarquía de headings
            del documento siga existiendo para screen readers. */}
        <h2 className="sr-only">{content.community.srTitle || "Comunidad UXUI"}</h2>
        <span className="tag">{content.sections.community.tag}</span>
      </div>
      <Appear>
        <div className="comunidad">
          <div className="comunidad-head">
            <div>
              <picture className="comunidad-logo">
                <source srcSet={encodeURI("assets/logo-cuxui.webp")} type="image/webp" />
                <img
                  src={encodeURI("assets/Logo comunidad uxui.png")}
                  alt={content.community.logoAlt || "Comunidad UXUI — logo"}
                  loading="lazy"
                  decoding="async"
                />
              </picture>
              <p className="comunidad-sub">{content.community.sub}</p>
            </div>
            <a className="btn" href={content.community.ctaHref} target="_blank" rel="noreferrer">
              {content.community.cta} <span className="arrow" aria-hidden="true">→</span>
            </a>
          </div>
          <div className="comunidad-grid">
            {content.community.stats.map((s) => (
              <div key={s.l} className="comunidad-stat">
                <div className="v">{s.v}</div>
                <div className="l">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </Appear>
    </section>
  );
}

// ─── Reviews ───────────────────────────────────────────────────────────────
// `content` puede ser el CONTENT[lang] completo (root) o sólo el subárbol
// home — soportamos ambos para no romper llamadas existentes.
function Reviews({ content, reviews, lang }) {
  const home = content.home || content;
  const L = (v) => (v && typeof v === "object" && (v.es || v.en) ? (v[lang] || v.es || v.en) : v);
  const labels = (content.reviewLabels) || {};
  return (
    <section className="section shell">
      <div className="section-head">
        <h2>{home.sections.reviews.title}</h2>
        <span className="tag">{home.sections.reviews.tag}</span>
      </div>
      <Appear className="reviews">
        {reviews.map((r, i) => {
          const label = labels[r.category] || r.label;
          return (
          <div
            className={`review${r.category ? ` review--${r.category}` : ""}`}
            key={i}
            style={{ animationDelay: `${i * 80}ms` }}
          >
            {label && <span className="review-label mono">{label}</span>}
            <blockquote>"{L(r.text)}"</blockquote>
            <div className="who">
              <span className="avatar">{r.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}</span>
              <span>
                <strong style={{ color: "var(--ink)" }}>{r.name}</strong><br />
                {L(r.role)}
              </span>
            </div>
          </div>
          );
        })}
      </Appear>
    </section>
  );
}

// ─── Metrics ───────────────────────────────────────────────────────────────
function Metrics({ content }) {
  return (
    <section className="section shell">
      <div className="section-head">
        <h2>{content.sections.metrics.title}</h2>
        <span className="tag">{content.sections.metrics.tag}</span>
      </div>
      <Appear>
        <div className="metrics">
          {content.metrics.items.map((m, i) => (
            <div className="metric" key={i}>
              <div className="l">{m.l}</div>
              <div className="v">
                {m.target != null ? <Counter target={m.target} /> : m.v}
              </div>
              <div className="sub">{m.sub}</div>
              {m.bars && <BarMini count={m.bars} />}
            </div>
          ))}
        </div>
      </Appear>
    </section>
  );
}

function BarMini({ count = 10 }) {
  const [ref, inView] = useInView();
  const heights = React.useMemo(
    () => Array.from({ length: count }, () => 0.25 + Math.random() * 0.75),
    [count]
  );
  return (
    <div className="bar-mini" ref={ref}>
      {heights.map((h, i) => (
        <i key={i} className={i === heights.length - 1 ? "on" : ""} style={{
          height: `${inView ? h * 22 : 2}px`,
          transition: `height 700ms cubic-bezier(.2,.7,.2,1) ${i * 30}ms`
        }} />
      ))}
    </div>
  );
}

// ─── Natural desborde (inline, when user hasn't opened the side panel) ─────
function NaturalDesborde({ content, onOpenDesborde }) {
  return (
    <section className="section shell">
      <Appear>
        <div className="desborde-natural">
          <span className="tag tag-line">{content.naturalDesborde.tag}</span>
          <div className="dn-grid">
            <div className="dn-frame">
              <span>{content.naturalDesborde.framelabel}</span>
              <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center" }}>
                <button className="vsl-frame play" style={{ position: "static" }} onClick={onOpenDesborde}></button>
              </div>
            </div>
            <div>
              <h3><MonoWithAccent text={content.naturalDesborde.title} /></h3>
              <p>{content.naturalDesborde.body}</p>
              <button className="btn btn--teal" onClick={onOpenDesborde}>
                {content.naturalDesborde.cta}
              </button>
            </div>
          </div>
        </div>
      </Appear>
    </section>
  );
}

// ─── Social icons (SVG inline — sin dependencias, crisp en cualquier size) ─
function SocialIcon({ name }) {
  const common = { width: 18, height: 18, viewBox: "0 0 24 24", fill: "currentColor", "aria-hidden": "true" };
  if (name === "linkedin") {
    return (
      <svg {...common}>
        <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5V9h3v10zM6.5 7.7A1.7 1.7 0 116.5 4.3a1.7 1.7 0 010 3.4zM19 19h-3v-5c0-1.2-.4-2-1.5-2A1.6 1.6 0 0013 13.4V19h-3V9h3v1.4A3.3 3.3 0 0116 8.7c2.2 0 3 1.4 3 3.8V19z"/>
      </svg>
    );
  }
  if (name === "youtube") {
    return (
      <svg {...common}>
        <path d="M23 7.2a3 3 0 00-2.1-2.1C19 4.6 12 4.6 12 4.6s-7 0-8.9.5A3 3 0 001 7.2C.5 9.1.5 12 .5 12s0 2.9.5 4.8a3 3 0 002.1 2.1c1.9.5 8.9.5 8.9.5s7 0 8.9-.5a3 3 0 002.1-2.1c.5-1.9.5-4.8.5-4.8s0-2.9-.5-4.8zM9.8 15.4V8.6L15.6 12l-5.8 3.4z"/>
      </svg>
    );
  }
  if (name === "medium") {
    return (
      <svg {...common}>
        <path d="M2.846 6.887c.03-.295-.083-.586-.303-.784l-2.24-2.7v-.403h6.958l5.378 11.795 4.728-11.795H24v.403l-1.917 1.838c-.165.126-.247.333-.213.538v13.498c-.034.204.048.411.213.537l1.871 1.838v.403h-9.412v-.403l1.939-1.882c.19-.19.19-.246.19-.537V7.794L11.227 21.83h-.732L4.911 7.794v9.404c-.052.385.076.774.347 1.052l2.521 3.058v.404H.638v-.404l2.521-3.058c.27-.279.39-.67.327-1.052z"/>
      </svg>
    );
  }
  return null;
}

// ─── Footer (home — multi-column con CTA en brand y socials extra) ───────
function Footer({ content }) {
  const f = content.footer;
  return (
    <footer className="foot shell" role="contentinfo">
      <div className="foot-grid">
        <div className="foot-brand">
          <h3>Anita Sevilla Ull</h3>
          {f.ctaPrimary && (
            <a className="btn btn--solid foot-cta" href={f.ctaPrimary.href}>
              {f.ctaPrimary.label}
            </a>
          )}
        </div>
        {f.cols.map((c) => (
          <div key={c.h}>
            <h3>{c.h}</h3>
            <ul>{c.links.map((l) => {
              const link = typeof l === "string" ? { label: l, href: "#" } : l;
              const ext = link.external;
              const dl = link.download;
              return (
                <li key={link.label}>
                  <a
                    href={link.href}
                    {...(ext ? { target: "_blank", rel: "noreferrer noopener" } : {})}
                    {...(dl ? { download: dl } : {})}
                  >
                    {link.label}{ext ? <span aria-hidden="true"> ↗</span> : null}
                  </a>
                </li>
              );
            })}</ul>
          </div>
        ))}
      </div>
    </footer>
  );
}

// ─── Footer condensed para páginas de caso ──────────────────────────────
// Una sola fila: Contratame (primario) izquierda + "Ver {otro caso}"
// (secundario) derecha. Pensado para navegar entre los pocos casos
// publicados sin pasar por el home.
function FooterCaseCondensed({ otherCaseLabel, otherCaseHref, primary, lang = "es" }) {
  const fallback = lang === "en"
    ? { label: "Hire me →", href: "mailto:anabelensevillaull@gmail.com" }
    : { label: "Contratame →", href: "mailto:anabelensevillaull@gmail.com" };
  const p = primary || fallback;
  return (
    <footer className="foot-case-condensed shell" role="contentinfo">
      <a className="btn btn--solid" href={p.href}>{p.label}</a>
      {otherCaseHref && (
        <a className="btn" href={otherCaseHref}>
          {otherCaseLabel} <span className="arrow" aria-hidden="true">→</span>
        </a>
      )}
    </footer>
  );
}

// ─── Footer extra condensed: SOLO íconos sociales, sutil y bajito. ───────
function FooterCondensed({ content }) {
  const f = content.footer;
  if (!f.socials) return null;
  return (
    <footer className="foot foot--condensed shell" role="contentinfo" aria-label="Redes sociales">
      <ul className="foot-socials">
        {f.socials.map((s) => (
          <li key={s.name}>
            <a
              className="foot-social"
              href={s.href}
              target="_blank"
              rel="noreferrer noopener"
              aria-label={s.name}
              title={s.name}
            >
              <SocialIcon name={s.icon} />
            </a>
          </li>
        ))}
      </ul>
    </footer>
  );
}

window.Hero = Hero;
window.Bento = Bento;
window.Bio = Bio;
window.Comunidad = Comunidad;
window.Reviews = Reviews;
window.Metrics = Metrics;
window.NaturalDesborde = NaturalDesborde;
window.Footer = Footer;
window.FooterCondensed = FooterCondensed;
window.FooterCaseCondensed = FooterCaseCondensed;
